import { useEffect, useState } from 'react';

import { ShiftsService } from '../../api/ShiftsAPI';
import s from '../../styles/staff.module.css';

// Changing a clock time, as its own modal.
//
// It asks for times rather than timestamps: the day is already settled by the
// card this was opened from, and a datetime field made somebody re-enter —
// and so re-check — a date nobody was changing.
//
// A clock-out earlier in the day than the clock-in is read as the next
// morning, which is what a shift that runs past midnight looks like. The
// alternative is refusing a shift that genuinely ended at 1 AM.
//
// The reason is stored as the shift's note, so the correction says why it was
// made and not only that it was made.

const REASONS = ['Forgot to clock out', 'Forgot to clock in', 'Wrong time', 'Approved by phone'];

const tm = (v) => (v ? new Date(v).toLocaleTimeString('en-US',
  { hour: 'numeric', minute: '2-digit' }) : '—');
const dayLabel = (key) => new Date(`${key}T00:00:00Z`).toLocaleDateString('en-US',
  { weekday: 'short', month: 'short', day: 'numeric', timeZone: 'UTC' });

// "18:42" in the viewer's own zone, which is what a time field reads and writes.
const toTimeInput = (v) => {
  if (!v) return '';
  const d = new Date(v);
  return `${String(d.getHours()).padStart(2, '0')}:${String(d.getMinutes()).padStart(2, '0')}`;
};

// A time field put back onto the day the entry belongs to.
const atTime = (sameDayAs, value) => {
  const [h, m] = value.split(':').map(Number);
  const d = new Date(sameDayAs);
  d.setHours(h, m, 0, 0);
  return d;
};

export function EditClockModal({ person, day, plan, entry, onClose, onSaved }) {
  const [clockIn, setClockIn] = useState(toTimeInput(entry.clockInAt));
  const [clockOut, setClockOut] = useState(toTimeInput(entry.clockOutAt));
  // The likeliest reason given the state of the entry, still changeable.
  const [reason, setReason] = useState(entry.clockOutAt ? 'Wrong time' : 'Forgot to clock out');
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState('');

  useEffect(() => {
    const onKey = (e) => { if (e.key === 'Escape' && !busy) onClose(); };
    window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
  }, [onClose, busy]);

  const save = async () => {
    if (!clockIn) { setError('A clock-in time is required.'); return; }
    const startedAt = atTime(entry.clockInAt, clockIn);
    let endedAt = null;
    if (clockOut) {
      endedAt = atTime(entry.clockInAt, clockOut);
      // Ran past midnight.
      if (endedAt <= startedAt) endedAt.setDate(endedAt.getDate() + 1);
    }

    setBusy(true);
    setError('');
    try {
      await ShiftsService.correct(entry.idShift, {
        clockInAt: startedAt.toISOString(),
        // Explicitly null rather than omitted: an empty clock-out means the
        // shift is open again, which is a real state and not "leave it".
        clockOutAt: endedAt ? endedAt.toISOString() : null,
        note: reason || undefined,
      });
      onSaved();
    } catch (err) {
      setError(err?.error || err?.message || 'That did not go through.');
      setBusy(false);
    }
  };

  return (
    <div className={s.editClockOverlay} onClick={busy ? undefined : onClose}>
      <div className={s.editClockModal} role="dialog" aria-label="Edit clock times"
           onClick={(e) => e.stopPropagation()}>
        <div className={s.editClockHead}>
          <div className={s.editClockWho}>
            <span className={s.shiftLabel}>Edit clock times</span>
            <span className={s.dayTitle}>{person.name}</span>
            <span className={s.daySub}>
              {dayLabel(day)}
              {plan ? ` · scheduled ${tm(plan.startAt)} – ${tm(plan.endAt)}` : ' · not scheduled'}
            </span>
          </div>
          <button className={s.schedClose} onClick={onClose} aria-label="Close">×</button>
        </div>

        <div className={s.editClockBody}>
          <div className={s.schedFieldRow}>
            <label className={s.schedField}>
              <span className={s.shiftLabel}>Clock in</span>
              <input className={s.schedInput} type="time" value={clockIn} disabled={busy}
                     onChange={(e) => setClockIn(e.target.value)} />
            </label>
            <label className={s.schedField}>
              <span className={s.shiftLabel}>Clock out</span>
              <input className={s.schedInput} type="time" value={clockOut} disabled={busy}
                     onChange={(e) => setClockOut(e.target.value)} />
            </label>
          </div>

          <div className={s.editClockReason}>
            <span className={s.shiftLabel}>Reason</span>
            <div className={s.editClockChips}>
              {REASONS.map(r => (
                <button key={r} type="button" disabled={busy}
                        className={`${s.editClockChip} ${reason === r ? s.editClockChipOn : ''}`}
                        onClick={() => setReason(reason === r ? '' : r)}>
                  {r}
                </button>
              ))}
            </div>
          </div>

          <p className={s.schedPanelNote}>
            Leave clock out empty if they are still on shift. The entry is
            marked Edited in the timesheet.
          </p>

          {error && <div className={s.attnError}>{error}</div>}

          <button className={`${s.schedPost} ${s.editClockSave}`} disabled={busy} onClick={save}>
            {busy ? 'Saving…' : 'Save times'}
          </button>
        </div>
      </div>
    </div>
  );
}
